import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Row,
  Col,
  Select,
  Spin,
  message,
  Button,
  Table,
  Empty,
  Tabs,
  DatePicker
} from 'antd';
import {
  ArrowLeftOutlined,
  RadarChartOutlined,
  BarChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface ClassComparison {
  class_name: string;
  student_count: number;
  avg_score: number;
  highest_score: number;
  lowest_score: number;
}

interface KnowledgeRadar {
  name: string;
  accuracy: number;
  total: number;
  correct: number;
}

interface TrendData {
  date: string;
  test_count: number;
  student_count: number;
  avg_score: number;
}

const AdvancedStatistics: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [classComparison, setClassComparison] = useState<ClassComparison[]>([]);
  const [knowledgeRadar, setKnowledgeRadar] = useState<KnowledgeRadar[]>([]);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    if (testId) {
      fetchClassComparison();
      fetchKnowledgeRadar();
    }
    fetchTrends();
  }, [testId, days]);

  useEffect(() => {
    if (testId) {
      fetchKnowledgeRadar();
    }
  }, [selectedClass, testId]);

  const fetchClassComparison = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/statistics/${testId}/class-comparison`);
      setClassComparison(response.data);
    } catch (error) {
      console.error('Failed to fetch class comparison');
    } finally {
      setLoading(false);
    }
  };

  const fetchKnowledgeRadar = async () => {
    try {
      const params: any = {};
      if (selectedClass) params.class_name = selectedClass;
      
      const response = await axios.get(`/api/statistics/${testId}/knowledge-radar`, { params });
      setKnowledgeRadar(response.data);
    } catch (error) {
      console.error('Failed to fetch knowledge radar');
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await axios.get('/api/statistics/trends', { params: { days } });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends');
    }
  };

  const classColumns = [
    { title: 'Class', dataIndex: 'class_name', key: 'class_name' },
    { title: 'Students', dataIndex: 'student_count', key: 'student_count' },
    { 
      title: 'Avg Score', 
      dataIndex: 'avg_score', 
      key: 'avg_score',
      render: (val: number) => `${val?.toFixed(1) || 0}%`,
      sorter: (a: ClassComparison, b: ClassComparison) => a.avg_score - b.avg_score
    },
    { 
      title: 'Highest', 
      dataIndex: 'highest_score', 
      key: 'highest_score',
      render: (val: number) => `${val?.toFixed(1) || 0}%`
    },
    { 
      title: 'Lowest', 
      dataIndex: 'lowest_score', 
      key: 'lowest_score',
      render: (val: number) => `${val?.toFixed(1) || 0}%`
    }
  ];

  const RadarChart: React.FC<{ data: KnowledgeRadar[] }> = ({ data }) => {
    if (!data || data.length === 0) {
      return <Empty description="No knowledge point data" />;
    }

    const maxVal = 100;
    const centerX = 200;
    const centerY = 200;
    const radius = 150;
    const levels = 5;
    const angleStep = (2 * Math.PI) / data.length;

    const getPoint = (index: number, value: number) => {
      const angle = index * angleStep - Math.PI / 2;
      const r = (value / maxVal) * radius;
      return {
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      };
    };

    const polygonPoints = data.map((d, i) => {
      const point = getPoint(i, d.accuracy);
      return `${point.x},${point.y}`;
    }).join(' ');

    return (
      <svg width="400" height="400" viewBox="0 0 400 400">
        {[...Array(levels)].map((_, i) => {
          const levelRadius = ((i + 1) / levels) * radius;
          const points = data.map((_, j) => {
            const angle = j * angleStep - Math.PI / 2;
            return `${centerX + levelRadius * Math.cos(angle)},${centerY + levelRadius * Math.sin(angle)}`;
          }).join(' ');
          return (
            <polygon
              key={i}
              points={points}
              fill="none"
              stroke="#e8e8e8"
              strokeWidth="1"
            />
          );
        })}
        
        {data.map((_, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const x2 = centerX + radius * Math.cos(angle);
          const y2 = centerY + radius * Math.sin(angle);
          return (
            <line
              key={i}
              x1={centerX}
              y1={centerY}
              x2={x2}
              y2={y2}
              stroke="#e8e8e8"
              strokeWidth="1"
            />
          );
        })}
        
        <polygon
          points={polygonPoints}
          fill="rgba(24, 144, 255, 0.3)"
          stroke="#1890ff"
          strokeWidth="2"
        />
        
        {data.map((d, i) => {
          const point = getPoint(i, d.accuracy);
          return (
            <circle
              key={i}
              cx={point.x}
              cy={point.y}
              r="5"
              fill="#1890ff"
            />
          );
        })}
        
        {data.map((d, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const labelRadius = radius + 30;
          const x = centerX + labelRadius * Math.cos(angle);
          const y = centerY + labelRadius * Math.sin(angle);
          return (
            <text
              key={i}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fill="#333"
            >
              {d.name.length > 8 ? d.name.substring(0, 8) + '...' : d.name}
              <tspan x={x} dy="14" fontSize="10" fill="#666">
                {d.accuracy}%
              </tspan>
            </text>
          );
        })}
      </svg>
    );
  };

  const TrendChart: React.FC<{ data: TrendData[] }> = ({ data }) => {
    if (!data || data.length === 0) {
      return <Empty description="No trend data available" />;
    }

    const width = 600;
    const height = 300;
    const padding = { top: 40, right: 40, bottom: 50, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const maxScore = Math.max(...data.map(d => d.avg_score || 0), 100);
    const maxStudents = Math.max(...data.map(d => d.student_count || 0), 10);

    const getX = (index: number) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
    const getYScore = (val: number) => padding.top + chartHeight - (val / maxScore) * chartHeight;
    const getYStudents = (val: number) => padding.top + chartHeight - (val / maxStudents) * chartHeight;

    const scorePath = data.map((d, i) => {
      const x = getX(i);
      const y = getYScore(d.avg_score || 0);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');

    const studentPath = data.map((d, i) => {
      const x = getX(i);
      const y = getYStudents(d.student_count || 0);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height}>
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#e8e8e8" />
        <line x1={padding.left} y1={padding.top + chartHeight} x2={padding.left + chartWidth} y2={padding.top + chartHeight} stroke="#e8e8e8" />
        
        {[0, 25, 50, 75, 100].map(val => (
          <g key={val}>
            <line
              x1={padding.left}
              y1={getYScore(val)}
              x2={padding.left + chartWidth}
              y2={getYScore(val)}
              stroke="#f0f0f0"
              strokeDasharray="4"
            />
            <text x={padding.left - 10} y={getYScore(val)} textAnchor="end" fontSize="10" fill="#666">
              {val}%
            </text>
          </g>
        ))}
        
        <path d={scorePath} fill="none" stroke="#1890ff" strokeWidth="2" />
        <path d={studentPath} fill="none" stroke="#52c41a" strokeWidth="2" />
        
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={getX(i)} cy={getYScore(d.avg_score || 0)} r="4" fill="#1890ff" />
            <circle cx={getX(i)} cy={getYStudents(d.student_count || 0)} r="4" fill="#52c41a" />
          </g>
        ))}
        
        {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0).map((d, i, arr) => {
          const originalIndex = data.indexOf(d);
          return (
            <text
              key={i}
              x={getX(originalIndex)}
              y={padding.top + chartHeight + 20}
              textAnchor="middle"
              fontSize="10"
              fill="#666"
            >
              {d.date.substring(5)}
            </text>
          );
        })}
        
        <g transform={`translate(${width - 120}, 20)`}>
          <line x1="0" y1="0" x2="20" y2="0" stroke="#1890ff" strokeWidth="2" />
          <text x="25" y="4" fontSize="10" fill="#666">Avg Score</text>
          <line x1="0" y1="15" x2="20" y2="15" stroke="#52c41a" strokeWidth="2" />
          <text x="25" y="19" fontSize="10" fill="#666">Students</text>
        </g>
      </svg>
    );
  };

  return (
    <div style={{ padding: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' }}>
      <Helmet>
        <title>Advanced Statistics – Smart Test Statistics System</title>
        <meta name="description" content="Advanced exam analytics: class comparison, knowledge-point radar chart, and score trend analysis." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <h2 style={{ margin: 0 }}>Advanced Statistics</h2>
        </div>

        <Spin spinning={loading}>
          <Tabs defaultActiveKey="class" items={[
            {
              key: 'class',
              label: <span><BarChartOutlined /> Class Comparison</span>,
              children: (
                <Card title="Class Performance Comparison">
                  {classComparison.length > 0 ? (
                    <>
                      <Table
                        columns={classColumns}
                        dataSource={classComparison}
                        rowKey="class_name"
                        pagination={false}
                      />
                      <div style={{ marginTop: 24 }}>
                        <h4>Score Distribution by Class</h4>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {classComparison.map(c => (
                            <Card key={c.class_name} size="small" style={{ minWidth: 200 }}>
                              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{c.class_name}</div>
                              <div style={{ position: 'relative', height: 20, background: '#f0f0f0', borderRadius: 4 }}>
                                <div
                                  style={{
                                    position: 'absolute',
                                    height: '100%',
                                    width: `${c.avg_score}%`,
                                    background: c.avg_score >= 80 ? '#52c41a' : c.avg_score >= 60 ? '#faad14' : '#ff4d4f',
                                    borderRadius: 4
                                  }}
                                />
                              </div>
                              <div style={{ textAlign: 'center', marginTop: 4, fontSize: 12 }}>
                                {c.avg_score?.toFixed(1)}%
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Empty description="No class data available" />
                  )}
                </Card>
              )
            },
            {
              key: 'radar',
              label: <span><RadarChartOutlined /> Knowledge Radar</span>,
              children: (
                <Card 
                  title="Knowledge Point Mastery"
                  extra={
                    classComparison.length > 0 && (
                      <Select
                        placeholder="Filter by class"
                        allowClear
                        style={{ width: 200 }}
                        value={selectedClass}
                        onChange={setSelectedClass}
                      >
                        {classComparison.map(c => (
                          <Option key={c.class_name} value={c.class_name}>{c.class_name}</Option>
                        ))}
                      </Select>
                    )
                  }
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <RadarChart data={knowledgeRadar} />
                      </div>
                    </Col>
                    <Col span={12}>
                      <Table
                        size="small"
                        columns={[
                          { title: 'Knowledge Point', dataIndex: 'name', key: 'name' },
                          { title: 'Accuracy', dataIndex: 'accuracy', key: 'accuracy', render: (v: number) => `${v}%` },
                          { title: 'Correct/Total', key: 'ratio', render: (_: any, r: KnowledgeRadar) => `${r.correct}/${r.total}` }
                        ]}
                        dataSource={knowledgeRadar}
                        rowKey="name"
                        pagination={false}
                      />
                    </Col>
                  </Row>
                </Card>
              )
            },
            {
              key: 'trends',
              label: <span><LineChartOutlined /> Trend Analysis</span>,
              children: (
                <Card 
                  title="Historical Trends"
                  extra={
                    <Select value={days} onChange={setDays} style={{ width: 120 }}>
                      <Option value={7}>Last 7 days</Option>
                      <Option value={30}>Last 30 days</Option>
                      <Option value={90}>Last 90 days</Option>
                    </Select>
                  }
                >
                  <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                    <TrendChart data={trends} />
                  </div>
                  <Table
                    size="small"
                    style={{ marginTop: 24 }}
                    columns={[
                      { title: 'Date', dataIndex: 'date', key: 'date' },
                      { title: 'Tests', dataIndex: 'test_count', key: 'test_count' },
                      { title: 'Students', dataIndex: 'student_count', key: 'student_count' },
                      { title: 'Avg Score', dataIndex: 'avg_score', key: 'avg_score', render: (v: number) => `${v?.toFixed(1) || 0}%` }
                    ]}
                    dataSource={trends}
                    rowKey="date"
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              )
            }
          ]} />
        </Spin>
      </Card>
    </div>
  );
};

export default AdvancedStatistics;
